import type { EmbedStrings } from '../strings';
import { fillTemplate } from '../strings';

type SchemaProperty = {
  description?: string;
  title?: string;
};

type Props = {
  name: string;
  // Customer-authored description from the tool catalog. Used as the card
  // heading because it's plain prose ("Adiciona N unidades ao carrinho")
  // versus `name` which is a function identifier.
  description?: string;
  // Tool inputSchema (JSON Schema). We only read `properties[k].description`
  // and `properties[k].title` to render readable labels next to each value.
  inputSchema?: { properties?: Record<string, SchemaProperty> };
  input: unknown;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  strings: EmbedStrings;
};

/**
 * Renders an inline read-only card asking the user to approve a tool call
 * before the loader runs the customer's handler. Used for tools where the
 * customer set `autoRun: false` (anything that mutates state, charges money,
 * sends notifications, etc.).
 *
 * Args are rendered as a `<dl>` of label/value pairs using the schema's
 * per-property `description` (or `title`, or the raw key as a fallback) so
 * non-technical end users see "SKU: WIDGET-1" instead of `{"sku":"WIDGET-1"}`.
 * The raw JSON is still available behind a `<details>` disclosure for users
 * who want to verify the full payload.
 */
export function ToolConfirmCard({
  name,
  description,
  inputSchema,
  input,
  onConfirm,
  onCancel,
  busy,
  strings,
}: Props) {
  const heading = description ?? fillTemplate(strings.confirmTitle, { name });
  const ariaLabel = fillTemplate(strings.confirmCardLabel, { name });
  const entries = toEntries(input);

  return (
    <div className="kb-tool-confirm" role="group" aria-label={ariaLabel}>
      <div className="kb-tool-confirm__header">
        <span className="kb-tool-confirm__title">{heading}</span>
      </div>

      {entries.length > 0 ? (
        <dl className="kb-tool-confirm__args">
          {entries.map(([k, v]) => (
            <div key={k} className="kb-tool-confirm__arg-row">
              <dt className="kb-tool-confirm__arg-label">{labelFor(k, inputSchema)}</dt>
              <dd className="kb-tool-confirm__arg-value">{formatValue(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {entries.length > 0 ? (
        <details className="kb-tool-confirm__raw">
          <summary>JSON</summary>
          <pre>{safeStringify(input)}</pre>
        </details>
      ) : null}

      <div className="kb-tool-confirm__actions">
        <button
          type="button"
          className="kb-tool-confirm__btn kb-tool-confirm__btn--cancel"
          onClick={onCancel}
          disabled={busy}
        >
          {strings.confirmCancel}
        </button>
        <button
          type="button"
          className="kb-tool-confirm__btn kb-tool-confirm__btn--confirm"
          onClick={onConfirm}
          disabled={busy}
          autoFocus
        >
          {busy ? strings.confirmBusy : strings.confirmConfirm}
        </button>
      </div>
    </div>
  );
}

function toEntries(input: unknown): Array<[string, unknown]> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return [];
  return Object.entries(input as Record<string, unknown>);
}

function labelFor(
  key: string,
  schema: Props['inputSchema'],
): string {
  const prop = schema?.properties?.[key];
  return prop?.description ?? prop?.title ?? key;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // Object / array values render as compact JSON. Truncate to keep the
  // confirm card from growing unbounded if the LLM passes a large payload.
  const s = safeStringify(v);
  return s.length > 200 ? s.slice(0, 200) + '…' : s;
}

function safeStringify(value: unknown): string {
  try {
    const s = JSON.stringify(value, null, 2) ?? String(value);
    // Hard cap on the raw JSON disclosure too — LLMs can produce surprising
    // payload sizes and we don't want a single message to blow out the panel.
    return s.length > 4000 ? s.slice(0, 4000) + '\n…(truncated)' : s;
  } catch {
    return String(value);
  }
}
